import CreateButton from "../components/CreateButton"
import { useCallback, useEffect, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import api from '../api'
import { ReactComponent as CheckIcon } from '../assets/images/icons/ic-check.svg'
import { ReactComponent as CopyIcon } from '../assets/images/icons/ic-copy.svg'
import { ButtonStatus } from '../common/constants'
import HeaderFooterLayout from '../components/layouts/HeaderFooterLayout'
import Button from '../components/Button'
import Table from '../components/Table'
import Dialog from '../components/dialogs/Dialog'
import AlertDialog from '../components/dialogs/AlertDialog'
import MeatBallsMenu from '../components/MeatBallsMenu'
import Badge from '../components/Badge'
import { DialogStatus } from '../enums'
import typo from '../typography.module.css'
import styles from './Developers.module.css'

const Developers = () => {
  const navigate = useNavigate()
  const [keys, setKeys] = useState([])
  const [company, setCompany] = useState(null)
  const [loading, setLoading] = useState(false)

  const [openCreate, setOpenCreate] = useState(false)
  const [createStatus, setCreateStatus] = useState(null)

  const [createdSecret, setCreatedSecret] = useState(null)
  const [createdPublic, setCreatedPublic] = useState(null)
  const [openSecretDialog, setOpenSecretDialog] = useState(false)

  const [keyToRevoke, setKeyToRevoke] = useState(null)
  const [revokeDialogOpen, setRevokeDialogOpen] = useState(false)
  const [revokeStatus, setRevokeStatus] = useState(DialogStatus.Default)
  const [clientIdCopied, setClientIdCopied] = useState(false)
  const [secretCopied, setSecretCopied] = useState(false)

  const loadKeys = useCallback(async () => {
    setLoading(true)
    try {
      const response = await api.get('/company/developers/keys')
      setCompany(response?.company || null)
      setKeys(response?.keys || [])
    } catch (e) {
      console.error(e)
    }
    setLoading(false)
  }, [])

  useEffect(() => {
    loadKeys()
  }, [loadKeys])

  const onCreate = async () => {
    setCreateStatus(ButtonStatus.Loading)
    try {
      const response = await api.post('/company/developers/keys', {})
      setCreatedSecret(response?.secret || null)
      setCreatedPublic(response?.public || null)
      setOpenCreate(false)
      setOpenSecretDialog(true)
      setCreateStatus(ButtonStatus.Success)
      await loadKeys()
    } catch (e) {
      console.error(e)
      setCreateStatus(ButtonStatus.Error)
    }
  }

  const onRevokeClick = (key) => {
    setKeyToRevoke(key)
    setRevokeStatus(DialogStatus.Default)
    setRevokeDialogOpen(true)
  }

  const onConfirmRevoke = async () => {
    if (!keyToRevoke) {
      return
    }

    setRevokeStatus(DialogStatus.Loading)
    try {
      await api.delete(`/company/developers/keys/${keyToRevoke.id}`)
      await loadKeys()
      setRevokeStatus(DialogStatus.Success)
    } catch (e) {
      console.error(e)
      setRevokeStatus(DialogStatus.Error)
    }
  }

  const onCloseRevoke = () => {
    setRevokeDialogOpen(false)
    setKeyToRevoke(null)
    setRevokeStatus(DialogStatus.Default)
  }

  const onCopySecret = async () => {
    if (!createdSecret) {
      return
    }

    try {
      await navigator.clipboard.writeText(createdSecret)
      setSecretCopied(true)
      setTimeout(() => {
        setSecretCopied(false)
      }, 2000)
    } catch (e) {
      console.error(e)
    }
  }

  const onCopyClientId = async () => {
    if (!company?.client_id) {
      return
    }

    try {
      await navigator.clipboard.writeText(company.client_id)
      setClientIdCopied(true)
      setTimeout(() => {
        setClientIdCopied(false)
      }, 2000)
    } catch (e) {
      console.error(e)
    }
  }

  const getActions = (key) => {
    const actions = [
      {
        label: 'Dettaglio chiamate',
        onClick: () => navigate(`/developers/keys/${encodeURIComponent(key.public)}`),
      },
    ]

    if (key.active) {
      actions.push({
        label: 'Revoca',
        style: { color: 'var(--sf-red)' },
        onClick: () => onRevokeClick(key),
      })
    }

    return actions
  }

  return (
    <HeaderFooterLayout hideHeader>
      <div className={styles.container}>
        <div className={styles.section}>
          <div className={styles.sectionInner}>
            <div className={styles.header}>
              <div className={typo.title}>Developers</div>
              <div className={styles.actions}>
                <CreateButton onClick={() => setOpenCreate(true)}>
                  Aggiungi chiave

                </CreateButton>
              </div>
            </div>

            {company?.client_id && (
              <div className={styles.clientIdBox}>
                <span className={styles.clientIdLabel}>Client ID</span>
                <span className={styles.clientIdValue}>{company.client_id}</span>
                <Button appearance='text' onClick={onCopyClientId} style={{ padding: 0 }} aria-label='Copia client id'>
                  {clientIdCopied ? (
                    <CheckIcon style={{ width: '12px', height: '12px' }} />
                  ) : (
                    <CopyIcon style={{ width: '12px', height: '12px' }} />
                  )}
                </Button>
              </div>
            )}

            <div className={styles.descriptionBox}>
              Da questa pagina puoi gestire le tue chiavi per l'integrazione con i servizi di Starting Finance Business School.
              Premendo sul pulsante <b>AGGIUNGI CHIAVE</b> puoi creare una nuova chiave: la secret verra' visualizzata una sola volta,
              quindi copiala e conservala con cura in un sistema sicuro.<br></br>
              {' '}Consulta la nostra{' '}
              <span className={styles.descriptionLink} onClick={() => navigate('/developers/documentation')}>
                documentazione
              </span>
              {' '}per i dettagli sull'implementazione.
            </div>

            <Table
              appearance='minimal'
              columns={[
                { key: 'public', label: 'PUBLIC', type: 'string' },
                { key: 'created_at', label: 'CREATA IL', type: 'datetime' },
                { key: 'last_used', label: 'ULTIMO UTILIZZO', type: 'datetime' },
                { key: 'status', label: 'STATO', type: 'custom' },
                { key: 'actions', label: 'AZIONI', type: 'custom', handleClick: true },
              ]}
              loading={loading}
              elements={keys.map((key) => ({
                ...key,
                last_used: key.last_used || null,
                status: {
                  value: key.active ? 'active' : 'revoked',
                  type: 'string',
                  component: key.active
                    ? <Badge size={'small'} name={'ATTIVA'} color={'var(--sf-green)'} />
                    : <Badge size={'small'} name={'REVOCATA'} color={'var(--sf-orange)'} />,
                },
                actions: {
                  value: key.id,
                  type: 'custom',
                  component: <MeatBallsMenu actions={getActions(key)} style={{ padding: '0.5rem' }} />,
                },
              }))}
              onRowClick={(key) => navigate(`/developers/keys/${encodeURIComponent(key.public)}`)}
              defaultSort={{ key: 'created_at', direction: 'desc' }}
            />
          </div>
        </div>

        <Dialog
          open={openCreate}
          onClose={() => {
            setOpenCreate(false)
            setCreateStatus(null)
          }}
          title='Aggiungi Chiave'
          action={(
            <Button
              appearance='default'
              status={createStatus}
              style={{ padding: '0.8rem 1rem' }}
              onClick={onCreate}
            >
              SALVA CHIAVE
            </Button>
          )}
        >
          <div className={styles.createDialogContent}>
            <div className={styles.techBadge}>KEY PAIR</div>
            <div className={styles.helper}>
              Verrà generata una coppia di chiavi <b>pubblica</b> e <b>privata</b>, utilizzate per autenticare
              le integrazioni con i servizi di Business School.
            </div>
            <div className={styles.helper}>
              La chiave privata (secret) verrà mostrata una sola volta dopo la creazione: salvala in modo sicuro.
            </div>
          </div>
        </Dialog>

        <Dialog
          open={openSecretDialog}
          onClose={() => {
            setOpenSecretDialog(false)
            setCreatedSecret(null)
            setCreatedPublic(null)
            setSecretCopied(false)
          }}
          title='Nuova Chiave Creata'
        >
          <div className={`${styles.dialogContent} ${styles.fullWidthDialogContent}`}>
            <div className={styles.keysGrid}>
              <div className={styles.keyBlock}>
                <div className={styles.keyLabel}>CHIAVE PUBBLICA</div>
                <div className={styles.secretBox}>{createdPublic}</div>
              </div>

              <div className={styles.keyBlock}>
                <div className={styles.keyLabel}>CHIAVE SEGRETA</div>
                <div className={styles.secretBoxRow}>
                  <div className={styles.secretValue}>{createdSecret}</div>
                  <Button appearance='text' onClick={onCopySecret} style={{ padding: 0 }} aria-label='Copia chiave segreta'>
                    {secretCopied ? (
                      <CheckIcon style={{ width: '20px', height: '20px' }} />
                    ) : (
                      <CopyIcon style={{ width: '20px', height: '20px' }} />
                    )}
                  </Button>
                </div>
              </div>
            </div>

            <div className={styles.secretWarning}>
              Attenzione: salva ora la chiave segreta. Dopo la chiusura non verrà più mostrata.
            </div>

          </div>
        </Dialog>

        <AlertDialog
          open={revokeDialogOpen}
          title={revokeStatus === DialogStatus.Success ? 'Secret key revocata' : 'Revoca secret key'}
          text={revokeStatus === DialogStatus.Error
            ? 'Impossibile revocare la secret key. Riprova.'
            : `Sei sicuro di voler revocare ${keyToRevoke?.public || 'questa key'}?`
          }
          status={revokeStatus}
          onClose={onCloseRevoke}
          actions={revokeStatus === DialogStatus.Success
            ? [{ label: 'OK', onClick: onCloseRevoke }]
            : [
              { label: 'ANNULLA', onClick: onCloseRevoke, inverse: true },
              { label: 'SI, REVOCA', onClick: onConfirmRevoke },
            ]
          }
        />
      </div>
    </HeaderFooterLayout>
  )
}

export default Developers
